import { runAfterTransition, useResizeObserver } from "@react-aria/utils";
import * as React from "react";

/**
 * Hook that observes an element's size and writes directly to provided setters.
 * Useful for writing size directly to parent/context state without intermediate local state.
 *
 * @param ref Ref to the element to observe.
 * @param setHeight Setter function to update height.
 * @param setWidth Optional setter function to update width.
 */
export function useObserveElementSize<T extends Element = HTMLElement>(
  ref: React.RefObject<T | null>,
  setHeight: (h: number) => void,
  setWidth?: (w: number) => void,
) {
  const onResize = React.useCallback(() => {
    const boundingClientRect = ref.current?.getBoundingClientRect();

    if (boundingClientRect) {
      setHeight(boundingClientRect.height);
      setWidth?.(boundingClientRect.width);
    } else {
      setHeight(0);
      setWidth?.(0);
    }
  }, [ref, setHeight, setWidth]);

  useResizeObserver({ ref, onResize });

  React.useLayoutEffect(() => {
    // run initial measurement synchronously to avoid a flash of incorrect size on mount.
    onResize();

    // Make sure to resize again after all transitions have completed.
    // Give it a bit of time to ensure any transitions have been started.
    const timeout = setTimeout(() => {
      runAfterTransition(onResize);
    }, 100);

    return () => {
      clearTimeout(timeout);

      // set size to 0 on unmount
      setHeight(0);
      setWidth?.(0);
    };
  }, [onResize, setHeight, setWidth]);
}

/**
 * Hook that tracks the width and height of an element using ResizeObserver.
 *
 * @param ref Ref to the element to observe.
 * @returns An object with { height, width }
 */
export function useElementSize<T extends Element = HTMLElement>(
  ref: React.RefObject<T | null>,
) {
  const [height, setHeight] = React.useState(0);
  const [width, setWidth] = React.useState(0);

  useObserveElementSize(ref, setHeight, setWidth);

  return { height, width } as const;
}

/**
 * Hook that tracks the width of an element using ResizeObserver.
 *
 * @param ref Ref to the element to observe.
 * @returns The width of the element
 */
export function useElementWidth<T extends Element = HTMLElement>(
  ref: React.RefObject<T | null>,
) {
  const { width } = useElementSize(ref);
  return width;
}

/**
 * Hook that tracks the height of an element using ResizeObserver.
 *
 * @param ref Ref to the element to observe.
 * @returns The height of the element
 */
export function useElementHeight<T extends Element = HTMLElement>(
  ref: React.RefObject<T | null>,
) {
  const { height } = useElementSize(ref);
  return height;
}

/**
 * Hook that creates a ref and tracks the width and height of an element using ResizeObserver.
 *
 * @returns A tuple of [ref, { height, width }]
 */
export function useElementSizeWithRef<T extends Element = HTMLElement>() {
  const elementRef = React.useRef<T>(null);
  const size = useElementSize(elementRef);
  return [elementRef as React.RefObject<T>, size] as const;
}

/**
 * Hook that creates a ref and tracks the width of an element using ResizeObserver.
 *
 * @returns A tuple of [ref, width]
 */
export function useElementWidthWithRef<T extends Element = HTMLElement>() {
  const [ref, { width }] = useElementSizeWithRef<T>();
  return [ref, width] as const;
}

/**
 * Hook that creates a ref and tracks the height of an element using ResizeObserver.
 *
 * @returns A tuple of [ref, height]
 */
export function useElementHeightWithRef<T extends Element = HTMLElement>() {
  const [ref, { height }] = useElementSizeWithRef<T>();
  return [ref, height] as const;
}
